import HRLayout from "../../hr/HRLayout";
import useHRCopy from "../../hr/i18n/useHRCopy";
import { useState, useEffect } from "react";
import EducationDropdown from "../../components/EducationDropdown";
import SkillsInput from "../../components/SkillsInput";
import KVKKModal from "../../components/KVKKModal";
import axios from "axios";

export default function AdayHizmetlerim() {
  const t = useHRCopy().candidate;
  const [kvkkOpen, setKvkkOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState({type: false, text: ''});
  const [failureMessage, setFailureMessage] = useState({type: false, text: ''});
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    education: "",
    skills: [],
    cvFile: null,
    kvkk: false,
  });

  useEffect(() => {
    if (failureMessage.text) {
      const timer = setTimeout(() => {
        setFailureMessage({ type: false, text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [failureMessage]);

  useEffect(() => {
    if (successMessage.text) {
      const timer = setTimeout(() => {
        setSuccessMessage({ type: false, text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["application/pdf", "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, cvFile: t.errors.cvType }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, cvFile: t.errors.cvSize }));
        return;
      }
      setErrors((prev) => ({ ...prev, cvFile: null }));
      setForm({ ...form, cvFile: file });
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = t.errors.fullName;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) newErrors.email = t.errors.email;
    const phoneRegex = /^[0-9]{10,15}$/; // basic example
    if (!phoneRegex.test(form.phone)) newErrors.phone = t.errors.phone;
    if (!form.education) newErrors.education = t.errors.education;
    if (!form.cvFile) newErrors.cvFile = t.errors.cv;
    if (!form.kvkk) newErrors.kvkk = t.errors.kvkk;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    try {
      setLoading(true);
      const data = new FormData();
      data.append("fullName", form.fullName);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("education", form.education);
      data.append("skills", JSON.stringify(form.skills));
      data.append("cv", form.cvFile);
      data.append("kvkk", form.kvkk ? "true" : "false");

      await axios.post(`${process.env.REACT_APP_API_URL_CANDIDATE}/apply`, data, {
         headers: { "Content-Type": "multipart/form-data" },
       });

      setForm({
        fullName: "",
        email: "",
        phone: "",
        education: "",
        skills: [],
        cvFile: null,
        kvkk: false,
      });
      setSuccessMessage({type: true, text: t.success});
    } catch (error) {
      console.error("Submit error:", error);
      setFailureMessage({type: true, text: t.failure});
    } finally {
      setLoading(false);
    }
  };

  const Divider = ({ label }) => (
    <div className="flex items-center my-2">
      <div className="flex-grow border-t border-gray-200" />
      <span className="mx-4 text-gray-400 text-xs font-semibold">{label}</span>
      <div className="flex-grow border-t border-gray-200" />
    </div>
  );

  return (
    <HRLayout>
      <section className="max-w-3xl mx-auto py-12 px-6 w-full">
        <div className="w-full bg-white rounded-sm border border-line p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-ink mb-2 text-center tracking-[-0.02em]">{t.title}</h1>
          <p className="text-steel text-center mb-8">{t.lede}</p>

          <form onSubmit={handleSubmit} className="space-y-7">
            {/* Full name */}
            <div>
              <label htmlFor="candidate-fullName" className="block text-sm font-semibold text-gray-700 mb-1">{t.fullName} <span className="text-red-500">*</span></label>
              <input
                id="candidate-fullName"
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                className={`w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition shadow-sm bg-white font-medium text-gray-900 ${errors.fullName ? 'border-red-400' : ''}`}
                placeholder={t.fullNamePh}
                autoComplete="off"
              />
              {errors.fullName && <p className="text-red-500 text-xs mt-1 font-medium">{errors.fullName}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="candidate-email" className="block text-sm font-semibold text-gray-700 mb-1">{t.email} <span className="text-red-500">*</span></label>
              <input
                id="candidate-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={`w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition shadow-sm bg-white font-medium text-gray-900 ${errors.email ? 'border-red-400' : ''}`}
                placeholder={t.emailPh}
                autoComplete="off"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1 font-medium">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="candidate-phone" className="block text-sm font-semibold text-gray-700 mb-1">{t.phone} <span className="text-red-500">*</span></label>
              <input
                id="candidate-phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className={`w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition shadow-sm bg-white font-medium text-gray-900 ${errors.phone ? 'border-red-400' : ''}`}
                placeholder={t.phonePh}
                autoComplete="off"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1 font-medium">{errors.phone}</p>}
            </div>

            <Divider label={t.sectionEducation} />

            {/* Education */}
            <div>
              <EducationDropdown
                value={form.education}
                onChange={(e) => setForm({ ...form, education: e.target.value })}
                error={errors.education}
              />
            </div>

            {/* Skills */}
            <div>
              <SkillsInput
                value={form.skills}
                onChange={(skillsArray) => setForm({ ...form, skills: skillsArray })}
              />
            </div>

            <Divider label={t.sectionCv} />

            {/* CV Upload */}
            <div className={`w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition shadow-sm bg-white font-medium text-gray-900 ${errors.cvFile ? 'border-red-400' : ''}`}>
              <label htmlFor="candidate-cv" className="block text-sm font-semibold text-gray-700 mb-1">{t.cvLabel} <span className="text-red-500">*</span></label>
              <input
                id="candidate-cv"
                type="file"
                name="cvFile"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition"
              />
              {errors.cvFile && <p className="text-red-500 text-xs mt-1 font-medium">{errors.cvFile}</p>}
            </div>

            <Divider label={t.sectionKvkk} />

            {/* KVKK consent */}
            <div className="flex items-start space-x-3 bg-blue-50/60 rounded-lg p-3">
              <input
                id="candidate-kvkk"
                type="checkbox"
                name="kvkk"
                checked={form.kvkk}
                onChange={handleChange}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-400"
              />
              <label htmlFor="candidate-kvkk" className="text-sm text-gray-700">
                {t.kvkkText}{' '}
                <button
                  type="button"
                  onClick={() => setKvkkOpen(true)}
                  className="text-blue-600 underline hover:text-blue-800 ml-1 font-semibold"
                >
                  {t.kvkkRead}
                </button>
              </label>
            </div>
            {errors.kvkk && <p className="text-red-500 text-xs mt-1 font-medium">{errors.kvkk}</p>}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full px-6 py-3 rounded-sm font-bold transition-colors duration-300 text-lg tracking-wide
                ${loading ? "bg-platinum text-steel/60 cursor-not-allowed" : "bg-navy hover:bg-ink text-white"}
              `}
            >
              {loading ? t.submitting : t.submit}
            </button>

            {/* Success and failure message appears here */}
            {successMessage.type && (
              <div className="mt-4 text-center text-green-700 bg-green-50 border border-green-300 px-4 py-2 rounded-md font-semibold shadow-sm animate-fade-in">
                {successMessage.text}
              </div>
            )}

            {failureMessage.type && (
              <div className="mt-4 text-center text-red-700 bg-red-50 border border-red-300 px-4 py-2 rounded-md font-semibold shadow-sm animate-fade-in">
                {failureMessage.text}
              </div>
            )}
          </form>
          {kvkkOpen && <KVKKModal onClose={() => setKvkkOpen(false)} />}
        </div>
      </section>
    </HRLayout>
  );
}
